import React, { useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { cn } from '../lib/utils';
interface SwipefileReference {
  id: string;
  title: string;
  thumbnail: string;
  topic: string;
}
interface SwipefileReferencePickerProps {
  availableReferences: SwipefileReference[];
  selectedReferences: SwipefileReference[];
  onSelectionChange: (references: SwipefileReference[]) => void;
  maxSelections?: number;
}
export function SwipefileReferencePicker({
  availableReferences,
  selectedReferences,
  onSelectionChange,
  maxSelections
}: SwipefileReferencePickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredReferences = availableReferences.filter(ref => ref.title.toLowerCase().includes(searchQuery.toLowerCase()) || ref.topic.toLowerCase().includes(searchQuery.toLowerCase()));
  const isSelected = (id: string) => selectedReferences.some(ref => ref.id === id);
  const toggleReference = (reference: SwipefileReference) => {
    if (isSelected(reference.id)) {
      onSelectionChange(selectedReferences.filter(ref => ref.id !== reference.id));
    } else {
      if (maxSelections && selectedReferences.length >= maxSelections) {
        return;
      }
      onSelectionChange([...selectedReferences, reference]);
    }
  };
  const removeReference = (id: string) => {
    onSelectionChange(selectedReferences.filter(ref => ref.id !== id));
  };
  return <div className="space-y-4" data-id="element-622">
      {/* Selected References */}
      {selectedReferences.length > 0 && <div data-id="element-623">
          <p className="text-sm font-semibold text-gray-700 mb-2" data-id="element-624">
            Selected ({selectedReferences.length}
            {maxSelections ? `/${maxSelections}` : ''})
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-625">
            {selectedReferences.map(ref => <Badge key={ref.id} className="bg-[#139191] text-white hover:bg-[#139191]/90 pr-1 gap-2" data-id="element-626">
                {ref.title}
                <button onClick={() => removeReference(ref.id)} className="hover:bg-white/20 rounded-full p-0.5" data-id="element-627">
                  <X className="h-3 w-3" data-id="element-628" />
                </button>
              </Badge>)}
          </div>
        </div>}

      {/* Search */}
      <div className="relative" data-id="element-629">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-630" />
        <Input placeholder="Search swipefile..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-631" />
      </div>

      {/* Reference Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto" data-id="element-632">
        {filteredReferences.map(ref => {
        const selected = isSelected(ref.id);
        return <button key={ref.id} onClick={() => toggleReference(ref)} className={cn('relative group rounded-lg overflow-hidden border-2 transition-all', selected ? 'border-[#139191] shadow-md' : 'border-gray-200 hover:border-gray-300')} data-id="element-633">
              <div className="relative bg-gray-100" style={{
            aspectRatio: '9/16'
          }} data-id="element-634">
                <img src={ref.thumbnail} alt={ref.title} className="w-full h-full object-cover" data-id="element-635" />
                {selected && <div className="absolute inset-0 bg-[#139191]/20 flex items-center justify-center" data-id="element-636">
                    <div className="h-8 w-8 rounded-full bg-[#139191] flex items-center justify-center" data-id="element-637">
                      <Check className="h-5 w-5 text-white" data-id="element-638" />
                    </div>
                  </div>}
                <Badge className="absolute top-2 left-2 text-xs bg-black/70 text-white border-none" data-id="element-639">
                  {ref.topic}
                </Badge>
              </div>
              <div className="p-2 bg-white" data-id="element-640">
                <p className="text-xs font-medium text-gray-900 line-clamp-2" data-id="element-641">
                  {ref.title}
                </p>
              </div>
            </button>;
      })}
      </div>
    </div>;
}